// --------------------------
// IMPORTS
// --------------------------
import {  Form,  Row,  Col,  DatePicker,  message,  List,  Card,  Badge,  Skeleton} from "antd";
import {  CheckCircleOutlined,  ClockCircleOutlined,  CloseCircleOutlined,  ConsoleSqlOutlined,
  ExclamationCircleOutlined,  RedoOutlined} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import { useState, useEffect } from "react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import moment from "moment";
import { SlideshowLightbox } from "lightbox.js-react";
import "lightbox.js-react/dist/index.css";
import CustomLoader from "../../utils/CustomLoader";
import {  getPendingRequestDetailThunk,  updatePendingRequestsThunk} from "../../store/thunks/pendingRequestsThunk";
import useCurrentPath, { handleGlSearch } from "../../utils/Constants";
import SubmitButton from "../../components/atoms/Buttons/SubmitButton";
import media from "../../media";
import { getExpenseCodeListThunk, getProjectCodeListThunk } from "../../store/thunks/masterDataThunk";

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function PendingRequestDetail({
  detailPageRenderSource,
  isVisible,
  setIsVisible,
  recordKey,
  onCloseReset
}) {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const id = recordKey;  
  const { RangePicker } = DatePicker;

  // --------------------------
  // REDUX STATE
  // --------------------------
  let storeRole = useSelector((state) => state.auth.role);
  let storeToken = useSelector((state) => state.auth.accessToken);
  let storeLIUId = useSelector((state) => state.auth.lIUId);
  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn
  );
  const decryptedEmpID = useSelector((state) => state.auth.empID);
  
  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [selectedValues, setSelectedValues] = useState([]);
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [prevApproverNotSelected, setPrevApproverNotSelected] = useState(false);
  const [adminSelectedApprovers, setAdminSelectedApprovers] = useState([]);
  const [adminChangedExpenseCode, setAdminChangedExpenseCode] = useState({});
  const [photoIndex, setPhotoIndex] = useState(0);
  const [projectCodes, setProjectCodes] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [expenseValues, setExpenseValues] = useState();
  const [checkboxes, setCheckboxes] = useState([]);
  const [expensesResponse, setExpensesResponse] = useState();  
  const [currentStatus, setCurrentStatus] = useState(
    process.env.REACT_APP_PENDING_STATUS.toUpperCase()
  );
  const [reason, setReason] = useState(null);
  const [otherReason, setOtherReason] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [loading, setLoading] = useState(true);

  // --------------------------
  // LOCAL VARIABLES
  // --------------------------
  const approversStatusCount = expenseValues?.approvers?.filter(
    (approver) =>
      approver.status === process.env.REACT_APP_APPROVED_STATUS.toUpperCase()
  ).length;

  const images =
    expenseValues?.expensesDetails?.flatMap((detail) =>
      detail.mediaInfo
        ?.filter(
          (file) =>
            file?.savedFileType === "jpeg" ||
            file?.savedFileType === "jpg" ||
            file?.savedFileType === "png"
        )
        ?.map(
          (file) => process.env.REACT_APP_BACKEND + "/" + file?.savedFilepath
        )
    ) || [];

  const commonStatusValues = [
    {
      label: "Approved",
      value: process.env.REACT_APP_APPROVED_STATUS?.toUpperCase() || "APPROVED",
    },
    {
      label: "Sent Back",
      value:
        process.env.REACT_APP_SENTBACK_STATUS?.toUpperCase() || "SENT_BACK",
    },
    {
      label: "Canceled",
      value: process.env.REACT_APP_CANCELED_STATUS?.toUpperCase() || "CANCELED",
    },
  ];

  const acknowledgerID =
    expenseValues?.approvers &&
    expenseValues?.approvers.length === 1 &&
    expenseValues?.approvers[0]?.userID;
  const totalReimbursement = expenseValues?.totalReimbursement.toFixed(2);

  const hasViewAccess =
    storeRole === process.env.REACT_APP_AROLE
      ? expenseValues &&
        expenseValues?.approvers.filter((expenseValue) => {
          return expenseValue?.userID === storeLIUId;
        })
      : "";

  const showStatusDropdown =
    storeRole === process.env.REACT_APP_AROLE
      ? expenseValues &&
        expenseValues?.approvers.filter((expenseValue) => {
          return (
            expenseValue?.userID === storeLIUId &&
            expenseValue?.status ===
              process.env.REACT_APP_PENDING_STATUS.toUpperCase()
          );
        })
      : "";

  // --------------------------
  // HANDLERS
  // --------------------------
  const handleCheckboxChange = (index) => {
    setPrevApproverNotSelected(false);
    if (index > 0 && !checkboxes[index - 1]) {
      message.error(`Please check previous checkboxes first!`);
      setPrevApproverNotSelected(true);
      return;
    }

    // Update checkbox state
    const updatedCheckboxes = [...checkboxes];
    updatedCheckboxes[index] = !checkboxes[index];
    setCheckboxes(updatedCheckboxes);

    // Update selected values based on the current checkbox state
    const updatedSelectedValues =
      updatedCheckboxes &&
      updatedCheckboxes
        .map((checked, i) =>
          checked ? expenseValues?.approvers[i]?.additional?._id : null
        )
        .filter((id) => id !== null); // Filter out null values

    handleStatus(storeRole, updatedSelectedValues);
  };

  // Disable dates after the current date
  const disableFutureDates = (current) => {
    return current && current.isAfter(moment().endOf("day"), "day");
  };

  const handleImageClick = (filePath) => {
    const index = images.findIndex((img) => img === filePath);
    if (index !== -1) {
      setPhotoIndex(index);
      setIsOpen(true);
    }
  };
  const onFinish = () => {
    if (
      storeRole === process.env.REACT_APP_ADROLE &&
      prevApproverNotSelected === true
    ) {
      message.error("Please check previous checkboxes first!");
    } else if (
      storeRole === process.env.REACT_APP_ADROLE &&
      currentStatus === process.env.REACT_APP_APPROVED_STATUS.toUpperCase() &&
      expenseValues?.approvers.length > 1 &&
      adminSelectedApprovers.length === 0
    ) {
      //when there is more than one approver and it is not selected
      message.error("Please select first approver.");
    } else if (
      storeRole === process.env.REACT_APP_ADROLE &&
      currentStatus === process.env.REACT_APP_APPROVED_STATUS.toUpperCase() &&
      expenseValues?.approvers.length === 1 &&
      adminSelectedApprovers.length === 0
    ) {
      //when there is one approver and it is not selected
      message.error("Please select an approver.");
    } else if (
      storeRole === process.env.REACT_APP_ADROLE &&
      currentStatus === process.env.REACT_APP_APPROVED_STATUS.toUpperCase() &&
      approversStatusCount === 1 &&
      adminSelectedApprovers.length === 1 &&
      approversStatusCount != expenseValues?.approvers.length
    ) {
      //when there is one approver who approved and total number of approvers is not equals to approved approver
      message.error("Please select an approver.");
    } else if (
      currentStatus === process.env.REACT_APP_PENDING_STATUS.toUpperCase()
    ) {
      message.error("Please select the status.");
    } else if (
      currentStatus === process.env.REACT_APP_CANCELED_STATUS.toUpperCase() &&
      reason === null
    ) {
      message.error("Please provide the reason.");
    } else if (
      currentStatus === process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() &&
      reason === null &&
      otherReason === null
    ) {
      message.error("Please provide the reason.");
    } else if (
      (currentStatus === process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() ||
        currentStatus ===
          process.env.REACT_APP_CANCELED_STATUS.toUpperCase()) &&
      otherReason === "Others" &&
      reason === null
    ) {
      message.error("Others: Please provide the reason.");
    } else if (
      currentStatus === process.env.REACT_APP_CANCELED_STATUS.toUpperCase() &&
      reason === null
    ) {
      message.error("Please provide the reason.");
    } else {
      setShowLoader(true);

      dispatch(
        updatePendingRequestsThunk({
          role: storeRole,
          status: currentStatus,
          reason,
          projectCodeID: expenseValues?.projectCodeID?._id || null,
          historySource: "backend",
          historyAction: "update",
          historyUserID: storeLIUId,
          adminSelectedApprovers,
          adminChangedExpenseCode,
          route: currentPath,
          id,
        })
      )
        .then(() => {
          message.success("Expense updated successfully.");
          setShowLoader(false);
          navigate(`/${process.env.REACT_APP_DASHBOARD_PR_SEARCH_IN}`);
          setIsVisible(false);
        })
        .catch((error) => {
          setShowLoader(false);
        });
    }
  };
  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setShowLoader(false);
  };

  const getStatusBadge = (status, reason) => {
    switch (status) {
      case "APPROVED":
        return (
          <Badge
            status="success"
            text="Approved"
            icon={<CheckCircleOutlined />}
            className="approved-badge"
          />
        );
      case "PENDING":
        return (
          <Badge
            status="warning"
            text="Pending Approval: Request Sent"
            icon={<ClockCircleOutlined />}
            className="pending-badge"
          />
        );
      case "CANCELED":
        return (
          <Badge
            status="error"
            text={
              <span>
                <strong>Canceled</strong>: {reason}
              </span>
            }
            icon={<CloseCircleOutlined />}
            className="canceled-badge"
          />
        );
      case "SENTBACK":
        return (
          <Badge
            status="processing"
            text={
              <span>
                <strong>Sent Back</strong>: {reason}
              </span>
            }
            icon={<RedoOutlined />}
            className="sentBack-badge"
          />
        );
      default:
        return (
          <Badge
            status="default"
            text="Awaiting previous Approver's Approval"
            icon={<ExclamationCircleOutlined />}
          />
        );
    }
  };

  const handleStatus = (source, checkedValues) => {
    if (source === process.env.REACT_APP_AROLE) {
      // Filter to keep only those values whose status allows them to be checked
      const filteredValues =
        checkedValues &&
        checkedValues.filter((value) => {
          const approver = expenseValues?.approvers.find(
            (approver) => approver?.additional?._id === value
          );

          // Only include if the approver's status is not "APPROVED"
          return (
            approver &&
            approver.status !==
              process.env.REACT_APP_APPROVED_STATUS.toUpperCase()
          );
        });
    }
    if (source === process.env.REACT_APP_ADROLE) {
      setAdminSelectedApprovers(checkedValues);
      setSelectedValues(checkedValues);
    }
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  // Update checkboxes when expenseValues.approvers changes
  useEffect(() => {
    if (expenseValues?.approvers) {
      // Initialize checkboxes based on the approvers' status
      const initialCheckboxes = expenseValues?.approvers.map(
        (approver) =>
          approver.status ===
          process.env.REACT_APP_APPROVED_STATUS.toUpperCase()
      );
      setCheckboxes(initialCheckboxes);
    }
  }, [expenseValues]);

  // Effect to set default selected values based on some criteria
  useEffect(() => {
    // Assuming you want to select specific approvers by their userID
    const defaultSelected = expenseValues?.approvers
      ?.filter(
        (approver) =>
          approver.status ===
          process.env.REACT_APP_APPROVED_STATUS.toUpperCase()
      )
      .map((approver) => approver.userID); // Map to userIDs

    if (defaultSelected && defaultSelected.length > 0) {
      setSelectedValues(defaultSelected);
      setAdminSelectedApprovers(defaultSelected);
    }
  }, [expenseValues]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        detailPageRenderSource && setShowLoader(true);
        const [detailResponse, expenseCodesRes, projectCodesRes] =
          await Promise.all([
            dispatch(
              getPendingRequestDetailThunk({ route: currentPath, id })
            ).unwrap(),
            dispatch(getExpenseCodeListThunk()).unwrap(),
            dispatch(getProjectCodeListThunk()).unwrap(),
          ]);

        setExpenseValues(detailResponse);
        setExpensesResponse(detailResponse?.expensesDetails);

        form.setFieldsValue({
          empName: detailResponse?.empName,
          empID: detailResponse?.empID,
          departmentID: `${detailResponse?.departmentID?.name} - ${detailResponse?.departmentID?.code}`,
          expensePeriodFromDate: [
            detailResponse?.expensePeriodFromDate
              ? moment(detailResponse?.expensePeriodFromDate.split("T")[0])
              : null,
            detailResponse?.expensePeriodToDate
              ? moment(detailResponse?.expensePeriodToDate.split("T")[0])
              : null,
          ],
          businessPurpose: detailResponse?.businessPurpose,
        });

        setExpenseCodes(expenseCodesRes || []);
        setProjectCodes(projectCodesRes || []);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setShowLoader(false);
      }
    };
    fetchAll();
  }, [dispatch, currentPath, id]);

  //Approver - a1 / a2 id with status is pending or admin
  useEffect(() => {
    // Only check conditions once the data is available
    if (storeRole && expenseValues) {
      if (decryptedEmpID !== expenseValues?.empID) {
        if (hasViewAccess && hasViewAccess.length === 0) {
          setShouldRedirect(true);
        }
      }
      setIsLoading(false);
    }
  }, [storeRole, expenseValues]);


  if (shouldRedirect) {
    return <Navigate to="/dashboard" />;
  }
  
  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <>
        {isVisible && (
          <div className="addExpensePopupForm">
            <div className={`addExpensePopupFormWraper ${isVisible?"addExpensePopupFormWraper-visible":""}`}>            
              {showLoader ? <CustomLoader showLoader={showLoader} />:
              <>
                {/* {detailPageRenderSource && <CustomLoader showLoader={showLoader} />} */}
                <div className="expenseDetailTitle contentBottomPadding">
                  <h3 className="text-dark font-size-lg">
                    Pending Expense Details
                  </h3>
                  <button
                    onClick={() => {
                        setIsVisible(false);
                        if (onCloseReset) {
                          onCloseReset();  // ⬅ Parent will refresh list & clear search
                        }
                        !detailPageRenderSource && navigate('/pending-requests');
                      }
                    }
                    className="expenseDetailTitleClose"
                  >
                    <img src={media.popupCross.svg} alt="close" />
                  </button>
                </div>
                <div className="customLabelForm customExpenseLabelForm">
                  <Form
                    layout="vertical"
                    autoComplete="off"
                    className="form-validation-start"
                    form={form}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                  >
                    <Row className="addMainWrap" gutter={[18, 24]}>
                      <Col xs={24} md={12} xl={12}>
                        <Form.Item className="m-0" name="empID">
                          <label
                            htmlFor="empID"
                            className="block mb-1 font-medium"
                          >
                            Employee ID
                          </label>
                          <input
                            placeholder="Id"
                            className="form-control rounded-md"
                            defaultValue={expenseValues?.empID}
                            disabled
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12} xl={12}>
                        <Form.Item className="m-0" name="empName">
                          <label
                            htmlFor="empName"
                            className="block mb-1 font-medium"
                          >
                            Employee Name
                          </label>
                          <input
                            defaultValue={expenseValues?.empName}
                            disabled
                            placeholder="Employee Name"
                            className="form-control rounded-md"
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12} xl={12}>
                        <Form.Item className="m-0" name="departmentID">
                          <label
                            htmlFor="departmentID"
                            className="block mb-1 font-medium"
                          >
                            Department
                          </label>
                          <input
                            placeholder="Department"
                            className="form-control rounded-md"
                            disabled
                            value={`${expenseValues?.departmentID?.name} - ${expenseValues?.departmentID?.code}`}
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12} xl={12}>
                        <Form.Item className="m-0" name="expensePeriodFromDate">
                          <label
                            htmlFor="expensePeriodFromDate"
                            className="block mb-1 font-medium"
                          >
                            <span>
                              Expense Period: (From - To)<span className="mandatory-symbol">*</span>
                            </span>
                          </label>
                          <RangePicker
                            // disabledDate={disableFutureDates}
                            className="form-control rounded-md d-inline-flex"
                            format="DD-MM-YYYY"
                            value={[
                              expenseValues?.expensePeriodFromDate
                                ? moment(expenseValues?.expensePeriodFromDate)
                                : null,
                              expenseValues?.expensePeriodToDate
                                ? moment(expenseValues?.expensePeriodToDate)
                                : null,
                            ]}
                            disabled
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12} xl={12}>
                        <Form.Item className="m-0" name="projectCodeID">
                          <label
                            htmlFor="projectCodeID"
                            className="block mb-1 font-medium"
                          >
                            <span>
                              Project Code<span className="mandatory-symbol">*</span>
                            </span>
                          </label>
                          <input
                            className="form-control rounded-md"
                            disabled
                            defaultValue={
                              expenseValues?.projectCodeID?.code &&
                              expenseValues?.projectCodeID?.companyName
                                ? expenseValues?.projectCodeID?.code +
                                  " - " +
                                  expenseValues?.projectCodeID?.companyName
                                : "N/A"
                            }
                          />
                        </Form.Item>
                      </Col>

                      {expenseValues?.natureOfExpense && (
                        <Col xs={24} md={12} xl={12}>
                          <div className="ant-form-item-control-input-content">
                            <label className="mb-3" name="natureOfExpense">
                              Nature of Expense<span className="mandatory-symbol">*</span>
                            </label>
                            <input
                              className="form-control rounded-md"
                              disabled
                              defaultValue={expenseValues?.natureOfExpense}
                            />
                          </div>
                        </Col>
                      )}

                      <Col xs={24} md={24} xl={24}>
                        <Form.Item className="m-0" name="businessPurpose">
                          <label className="mb-3" name="businessPurpose">
                            <span>
                              Vendor Name<span className="mandatory-symbol">*</span>
                            </span>
                          </label>
                          <textarea
                            className="form-control rounded-md"
                            disabled
                            value={expenseValues?.businessPurpose || "N/A"}
                          ></textarea>
                        </Form.Item>
                      </Col>
                    </Row>

                    {/*Repeater Area*/}
                    <div className="repeter rounded-sm expenseAddRepeater">
                      {/* <Row gutter={[12, 12]} className="m-0 expenseListRepeater">
                      <Col sm={3} className="repeat-label">
                        <label className="mb-0">
                          <span className="mandatory-symbol">*</span>Date
                        </label>
                      </Col>
                      <Col sm={3} className="repeat-label">
                        <label className="mb-0">
                          <span className="mandatory-symbol">*</span>Description
                        </label>
                      </Col>
                      <Col sm={4} className="repeat-label">
                        <label className="mb-0">
                          <span className="mandatory-symbol">*</span>Department
                        </label>
                      </Col>
                      <Col sm={4} className="repeat-label">
                        <label className="mb-0">
                          <span className="mandatory-symbol">*</span>Expense Code
                        </label>
                      </Col>
                      <Col sm={3} className="repeat-label">
                        <label className="mb-0">
                          <span className="mandatory-symbol">*</span>Currency
                        </label>
                      </Col>
                      <Col sm={3} className="repeat-label">
                        <label className="mb-0">
                          <span className="mandatory-symbol">*</span>Cost
                        </label>
                      </Col>
                      <Col sm={4} className="repeat-label">
                        <label className="mb-0">Upload</label>
                      </Col>
                    </Row> */}
                      <Row className="expenseListTitle" gutter={[24, 24]}>
                        <div className="addExpenseSubGroup" style={{ marginTop: "2px"}}>
                          <h3>Add Expense Details</h3>
                        </div>
                      </Row>
                      {expenseValues?.expensesDetails &&
                        expenseValues?.expensesDetails.map((detail, index) => {
                          return (
                            <div className="repaetMargins">
                              <h3 className="text-dark font-size-lg expenseRepeaterDetailHeading">
                                {`Expense Detail #${index + 1}`}
                              </h3>

                              <Row
                                gutter={[18, 24]}
                                className="RepaetRow"
                                key={index}
                              >
                                <Col xs={24} md={12} xl={12}>
                                  <div className="form-row-wrap">
                                    <label
                                      htmlFor="Date"
                                      className="block mb-1 font-medium"
                                    >
                                      
                                      Date<span className="mandatory-symbol">*</span>
                                    </label>
                                    <span className="form-control rounded-md customDisabled">
                                      {moment
                                        .utc(detail?.date)
                                        .local()
                                        .format("DD-MM-YYYY")}
                                    </span>
                                  </div>
                                </Col>

                                <Col xs={24} md={12} xl={12}>
                                  <div className="form-row-wrap">
                                    <label
                                      htmlFor="Description"
                                      className="block mb-1 font-medium"
                                    >
                                    
                                      Description<span className="mandatory-symbol">*</span>
                                    </label>
                                    <span className="form-control h-auto rounded-md customDisabled">
                                      {detail?.description}
                                    </span>
                                  </div>
                                </Col>

                                <Col xs={24} md={12} xl={12}>
                                  <div className="form-row-wrap">
                                    <label
                                      htmlFor="Department"
                                      className="block mb-1 font-medium"
                                    >
                                      
                                      Department<span className="mandatory-symbol">*</span>
                                    </label>
                                    <span className="form-control h-auto rounded-md customDisabled">
                                      {`${detail.departmentID?.name} - ${detail.departmentID?.code}`}
                                    </span>
                                  </div>
                                </Col>

                                <Col xs={24} md={12} xl={12}>
                                  <div className="form-row-wrap">
                                    <label
                                      htmlFor="Expense Code"
                                      className="block mb-1 font-medium"
                                    >
                                      
                                      Expense Code<span className="mandatory-symbol">*</span>
                                    </label>
                                    <div
                                      title={`${detail.expenseCodeID?.costCode} - ${detail.expenseCodeID?.name} -- ${detail.expenseCodeID}`}
                                    >
                                      {storeRole ===
                                        process.env.REACT_APP_ADROLE &&
                                      !expenseValues?.isAdminChangedExpenseCode ? (
                                        <Select
                                          value={
                                            adminChangedExpenseCode[index]
                                              ?.new || {
                                              label: `${detail.expenseCodeID?.costCode} - ${detail.expenseCodeID?.name}`,
                                              value: detail.expenseCodeID?._id,
                                            }
                                          }
                                          onChange={
                                            (value) => {
                                              setAdminChangedExpenseCode(
                                                (prev) => ({
                                                  ...prev,
                                                  [index]: {
                                                    old: {
                                                      label: `${detail.expenseCodeID?.costCode} - ${detail.expenseCodeID?.name}`,
                                                      value:
                                                        detail.expenseCodeID?._id,
                                                    },
                                                    new: value,
                                                  },
                                                })
                                              );
                                            }
                                            //handleChange(index, "expenseCodeID", value)
                                          }
                                          className="rounded-md"
                                          options={expenseCodes}
                                        />
                                      ) : (
                                        <span className="form-control h-auto rounded-md customDisabled">
                                          {`${detail.expenseCodeID?.costCode} - ${detail.expenseCodeID?.name}`}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </Col>

                                <Col xs={24} md={12} xl={12}>
                                  <div className="form-row-wrap">
                                    <label
                                      htmlFor="Currency"
                                      className="block mb-1 font-medium"
                                    >
                                      
                                      Currency<span className="mandatory-symbol">*</span>
                                    </label>
                                    <span className="form-control rounded-md customDisabled">
                                      {detail.currencyID?.code
                                        ? detail.currencyID?.code
                                        : "N/A"}
                                    </span>
                                  </div>
                                </Col>

                                <Col xs={24} md={12} xl={12}>
                                  <div className="form-row-wrap">
                                    <label
                                      htmlFor="Cost"
                                      className="block mb-1 font-medium"
                                    >
                                      
                                      Cost<span className="mandatory-symbol">*</span>
                                    </label>
                                    <span className="form-control rounded-md customDisabled">
                                      {detail.cost}
                                    </span>
                                  </div>
                                </Col>

                                <Col sm={24}>
                                  {detail?.mediaInfo.map((file, index) => {
                                    const fileName = file?.savedFilepath
                                      ?.split(/[/\\]/)
                                      .pop();
                                    return (
                                      <div className="fileIcon" key={index}>
                                        <small>Document</small>
                                        {file?.savedFileType === "jpeg" ||
                                        file?.savedFileType === "jpg" ||
                                        file?.savedFileType === "png" ? (
                                          <SlideshowLightbox>
                                            <img
                                              key={index}
                                              src={`${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`}
                                              alt="media"
                                              width="100"
                                              height="100"
                                              style={{ cursor: "pointer" }}
                                              onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = media.pdfIcon.svg; // fallback image
                                              }}
                                              onClick={() =>
                                                handleImageClick(
                                                  `${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`
                                                )
                                              }
                                            />
                                          </SlideshowLightbox>
                                        ) : (
                                          <a
                                            href={
                                              process.env.REACT_APP_BACKEND +
                                              "/" +
                                              file?.savedFilepath
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            <img
                                              src={media.pdfIcon.svg}
                                              alt="File Icon"
                                            />
                                            <span>{fileName}</span>
                                          </a>
                                        )}
                                      </div>
                                    );
                                  })}
                                </Col>
                              </Row>
                            </div>
                          );
                        })}
                    </div>
                    {/*Repeater Area*/}

                    <Row className="expenseListAprovers">
                      <Col className="approverCol" xs={24} md={12} xl={12}>
                        {/* Card for Approvers */}
                        <Card
                          title="Approvers Status"
                          bordered={true}
                          style={{ marginBottom: "10px" }}
                        >
                          {expenseValues?.approvers && (
                            <List
                              itemLayout="horizontal"
                              dataSource={expenseValues?.approvers}
                              renderItem={(approver, index) => (
                                <List.Item>
                                  <List.Item.Meta
                                    title={
                                      <>
                                        <div className="spanApprovers">
                                          <span className="approvLabel">
                                            Approver Name:
                                          </span>

                                          <div className="imgTitle">
                                            {approver?.additional
                                              ?.profilePicUrl ? (
                                              <>
                                                <img
                                                  src={
                                                    process.env
                                                      .REACT_APP_BACKEND +
                                                    "/images/" +
                                                    approver?.additional
                                                      ?.profilePicUrl
                                                  }
                                                  alt="card icon"
                                                />
                                              </>
                                            ) : (
                                              <span className="initialLetter">
                                                {approver?.additional
                                                  ? approver?.additional?.name
                                                      .charAt(0)
                                                      .toUpperCase()
                                                  : "?"}
                                              </span>
                                            )}

                                            <span>
                                              <h4>
                                                {approver?.additional?.name}
                                              </h4>
                                              <small>
                                                {getStatusBadge(
                                                  approver?.status,
                                                  approver?.reason
                                                )}
                                              </small>
                                            </span>
                                          </div>
                                        </div>
                                      </>
                                    }
                                  />
                                </List.Item>
                              )}
                            />
                          )}
                        </Card>
                      </Col>

                      <Col className="approverCol" xs={24} md={12} xl={12}>
                        {/* Card for Admin Actions */}
                        <Card title="Admin Action Status" bordered={true}>
                          {expenseValues?.adminApprover &&
                          expenseValues?.adminApprover.length > 0 ? (
                            <List
                              itemLayout="horizontal"
                              dataSource={expenseValues?.adminApprover}
                              renderItem={(adminApprover, index) => (
                                <List.Item>
                                  <List.Item.Meta
                                    title={
                                      <>
                                        <span>
                                          <strong>Approver Name: </strong>
                                          {adminApprover?.userID?.name}
                                        </span>

                                        <div className="spanApprovers">
                                          <span className="approvLabel">
                                            Approver Name:
                                          </span>
                                          <div className="imgTitle">
                                            {adminApprover?.userID
                                              ?.profilePicUrl ? (
                                              <>
                                                <img
                                                  src={
                                                    process.env
                                                      .REACT_APP_BACKEND +
                                                    "/images/" +
                                                    adminApprover?.userID
                                                      ?.profilePicUrl
                                                  }
                                                  alt="card icon"
                                                />
                                              </>
                                            ) : (
                                              <span className="initialLetter">
                                                {adminApprover?.userID
                                                  ? adminApprover?.userID?.name
                                                      .charAt(0)
                                                      .toUpperCase()
                                                  : "?"}
                                              </span>
                                            )}
                                            <span>
                                              <h4>
                                                {adminApprover?.userID?.name}
                                              </h4>

                                              <small>
                                                {getStatusBadge(
                                                  adminApprover?.status,
                                                  adminApprover?.reason
                                                )}
                                              </small>
                                            </span>
                                          </div>
                                        </div>
                                      </>
                                    }
                                  />
                                </List.Item>
                              )}
                            />
                          ) : (
                            <div className="admin-status-awaiting">
                              <span>
                                Admin action will take place after all approvers
                                complete their approvals.
                              </span>
                            </div>
                          )}
                        </Card>
                      </Col>
                    </Row>

                    {/*Acknowledgement*/}
                    {expenseValues?.seekAcknowledgment?.[0]?.ackMessage !==
                      null && (
                      <Col sm={24}>
                        <Card
                        className="card-title-14"
                          title="Acknowledgement Received"
                          bordered={true}
                        >
                          {expenseValues?.seekAcknowledgment?.[0]?.ackMessage}
                        </Card>
                      </Col>
                    )}
                    
                      
                    {expenseValues?.seekAcknowledgment?.[0]?.sentAt !== null &&
                    expenseValues?.seekAcknowledgment?.[0]?.ackMessage ===
                      null ? (
                      <Col sm={24}>
                        <Card
                          title="Seek Acknowledgement"
                          bordered={true}
                          className="card-title-14"
                        >
                          <span>Request Sent</span>
                        </Card>
                      </Col>
                    ) : (
                      (storeRole === process.env.REACT_APP_ADROLE ||
                        (showStatusDropdown &&
                          showStatusDropdown.length > 0)) && (
                        <>
                          <Card
                          className="card-title-14"
                            title={
                              storeRole === process.env.REACT_APP_ADROLE
                                ? "Manage Approvers & Request Status"
                                : "Manage Request Status"
                            }
                            bordered={true}
                          >
                            <div className="">
                              <Row gutter={[18, 24]}>
                                <Col xs={24} md={12} xl={12}>
                                
                                  <div className="form-row-wrap">
                                    <label
                                      htmlFor="Status"
                                      className="block mb-1 font-medium"
                                    >
                                      
                                      Status
                                    </label>

                                  <Select
                                    menuPlacement="auto"
                                    className="rounded-md"
                                    placeholder="Select options"
                                    options={
                                      (expenseValues?.acknowledgerID !==
                                        acknowledgerID &&
                                        approversStatusCount) ===
                                        expenseValues?.approvers?.length &&
                                      storeRole ===
                                        process.env.REACT_APP_ADROLE &&
                                      expenseValues?.seekAcknowledgment[0]
                                        ?.ackMessage === null
                                        ? [
                                            {
                                              label: "Seek Acknowledgement",
                                              value:
                                                process.env.REACT_APP_SEEK_ACK_STATUS.replace(
                                                  " ",
                                                  ""
                                                ).toUpperCase(),
                                            },
                                            ...commonStatusValues,
                                          ]
                                        : commonStatusValues
                                    }
                                    onChange={(item) => {
                                      setCurrentStatus(item.value);
                                      setReason(null);
                                      setOtherReason(null);
                                    }}
                                  />
                                  </div>
                                </Col>

                                {(currentStatus ===
                                  process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() ||
                                  currentStatus ===
                                    process.env.REACT_APP_CANCELED_STATUS.toUpperCase()) && (
                                  <>
                                    {currentStatus ===
                                      process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() && (
                                      <Col xs={24} md={12} xl={12}>
                                        <div className="form-row-wrap">
                                    <label
                                      htmlFor="Reason"
                                      className="block mb-1 font-medium"
                                    >
                                      
                                      Reason<span className="mandatory-symbol">*</span>
                                    </label>
                                        <Select
                                          menuPlacement="auto"
                                          className="rounded-md"
                                          placeholder="Select options"
                                          options={[
                                            {
                                              label:
                                                "Incorrect Information Provided",
                                              value:
                                                "Incorrect Information Provided",
                                            },
                                            {
                                              label: "Invalid Document Uploaded",
                                              value: "Invalid Document Uploaded",
                                            },
                                            { label: "Others", value: "Others" },
                                          ]}
                                          onChange={(item) => {
                                            setOtherReason(item.value);
                                            if (item.value === "Others") {
                                              setReason(null);
                                            } else {
                                              setReason(item.value);
                                            }
                                          }}
                                        />
                                        </div>
                                      </Col>
                                    )}
                                    {(currentStatus ===
                                      process.env.REACT_APP_CANCELED_STATUS.toUpperCase() ||
                                      otherReason === "Others") && (
                                      <Col xs={24} md={24} xl={24}>
                                      
                                        <div className="form-row-wrap">
                                    <label
                                      htmlFor="Reason"
                                      className="block mb-1 font-medium"
                                    >
                                      
                                    {currentStatus ===
                                          process.env.REACT_APP_CANCELED_STATUS.toUpperCase()
                                            ? "Reason"
                                            : "Others"}<span className="mandatory-symbol">*</span>
                                    </label>

                                        <TextArea
                                          className="rounded-md"
                                          placeholder="Please enter the reason"
                                          onChange={(e) => {
                                            setReason(e.target.value);
                                          }}
                                        />
                                        </div>
                                      </Col>
                                    )}
                                  </>
                                )}
                              </Row>
                            </div>
                            {storeRole === process.env.REACT_APP_ADROLE &&
                              currentStatus ===
                                process.env.REACT_APP_APPROVED_STATUS.toUpperCase() && (
                                <>
                                  <div className="mt-5">
                                    <Row gutter={[18, 24]}>
                                      <Col sm={24}>
                                        {/* Approver Section */}
                                        <div className="approver-section">
                                          <h3 className="text-size-14">Select Approvers</h3>

                                          <div className="selectedApp">
                                            {expenseValues?.approvers.map(
                                              (approver, index) => {
                                                const isDisabled =
                                                  approver.status ===
                                                  process.env.REACT_APP_APPROVED_STATUS.toUpperCase();
                                                return (
                                                  <label
                                                    key={index}
                                                    className={
                                                      isDisabled
                                                        ? "disabled-label"
                                                        : ""
                                                    }
                                                  >
                                                    <span className="checkbox-container">
                                                      <input
                                                        type="checkbox"
                                                        checked={
                                                          checkboxes[index] ||
                                                          false
                                                        }
                                                        onChange={() => {
                                                          handleCheckboxChange(
                                                            index
                                                          );
                                                        }}
                                                        disabled={isDisabled} // Disable based on status
                                                      />
                                                    </span>
                                                    <span>
                                                      {approver?.additional?.name}
                                                    </span>
                                                  </label>
                                                );
                                              }
                                            )}
                                          </div>
                                        </div>
                                      </Col>
                                    </Row>
                                  </div>
                                </>
                              )}
                          </Card>
                        </>
                      )
                    )}
                    <div className="text-center footerBtn">
                      <div className="footerReversment">
                        <small>Total Reimbursement</small>
                        <h4>
                          {process.env.REACT_APP_DEFAULT_CURRENCY}
                          {totalReimbursement}
                        </h4>
                      </div>
                      <div className="btnsWrap">
                        <SubmitButton
                          ky="update"
                          type="primary"
                          htmlType="submit"
                          submitName="Update"
                          className="btn ml-auto d-block px-5 py-2 defaultbutton rounded-lg"
                        />
                      </div>
                    </div>
                  </Form>
                </div>
              </>}
            </div>
          </div>
        )}
      
    </>
  );
}
